/**
 * Background sync — refresh dashboard/wallet cache when idle (no scrapers).
 */
import { Capacitor } from '@capacitor/core';
import { perfTimeline } from '../utils/startupPerf';
import { getServiceWorkerRegistration } from './serviceWorkerRegistration';

export const BG_SYNC_EVENT = 'lockedin-bg-sync';
export const OFFLINE_QUEUE_FLUSH_EVENT = 'lockedin-offline-queue-flush';

const QUEUE_KEY = 'lockedin_offline_queue_v1';
const SYNC_TAGS = ['sync-dashboard', 'sync-wallet'];

function dispatchBgSync() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BG_SYNC_EVENT));
  window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_FLUSH_EVENT));
  perfTimeline('pwa.backgroundSync', 'pwa');
}

export function initBackgroundSyncListeners() {
  if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return () => {};

  const onSwMessage = (event) => {
    const type = event?.data?.type;
    if (type === 'BG_SYNC_REFRESH' || type === 'BG_SYNC_WALLET') {
      dispatchBgSync();
    }
  };

  navigator.serviceWorker?.addEventListener('message', onSwMessage);

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      registerBackgroundSync();
    }
  };
  document.addEventListener('visibilitychange', onVisible);

  window.addEventListener('online', registerBackgroundSync);

  registerBackgroundSync();

  return () => {
    navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', registerBackgroundSync);
  };
}

export async function registerBackgroundSync() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await getServiceWorkerRegistration();
  if (!reg?.sync) return;

  await Promise.all(
    SYNC_TAGS.map((tag) =>
      reg.sync.register(tag).catch(() => {
        // Background Sync not supported — client event still fires on visibility
      })
    )
  );
}

export function readOfflineQueue() {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

export function writeOfflineQueue(queue) {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-20)));
  } catch (_) {}
}

/** Queue a failed mutating API call for retry on next background sync. */
export function enqueueFailedRequest(entry) {
  const queue = readOfflineQueue();
  queue.push({ ...entry, at: Date.now() });
  writeOfflineQueue(queue);
  registerBackgroundSync();
}

export async function flushOfflineQueue(fetchFn) {
  const queue = readOfflineQueue();
  if (!queue.length) return;

  const remaining = [];
  for (const item of queue) {
    try {
      const ok = await fetchFn(item);
      if (!ok) remaining.push(item);
    } catch (_) {
      remaining.push(item);
    }
  }
  writeOfflineQueue(remaining);
}
